import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Briefcase, ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import { API } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const STATUS_LABEL = {
  new: "Eingereicht", in_review: "In Prüfung", accepted: "Angenommen", rejected: "Abgelehnt",
};

export default function Jobs() {
  const { userToken } = useAuth();
  const pd = usePageDescription("jobs", {
    title: "Jobs bei Habthek",
    intro: "Du liebst Habbo und willst mitgestalten? Hier findest du alle offenen Positionen – bewirb dich direkt mit deinem Habthek-Account.",
    keywords: ["Redaktion", "Events", "Design", "Community"],
  });
  const [data, setData] = useState(null);
  const [category, setCategory] = useState(null);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    axios.get(`${API}/jobs`).then((r) => setData(r.data)).catch(() => setData({ items: [], categories: [] }));
  }, []);

  useEffect(() => {
    if (!userToken) { setMine([]); return; }
    axios.get(`${API}/jobs/applications/mine`, { headers: { Authorization: `Bearer ${userToken}` } })
      .then((r) => setMine(r.data || [])).catch(() => {});
  }, [userToken]);

  const appliedMap = useMemo(() => {
    const m = {};
    mine.forEach((a) => { m[a.job_id] = a.status; });
    return m;
  }, [mine]);

  const items = useMemo(() => {
    if (!data) return [];
    return category ? data.items.filter((j) => j.category === category) : data.items;
  }, [data, category]);

  return (
    <section className="mx-auto max-w-6xl px-4 py-14" data-testid="jobs-page">
      <PageHero
        eyebrow={pd.eyebrow || "Werde Teil des Teams"}
        icon={Briefcase}
        title={pd.title}
        keywords={pd.keywords}
        intro={pd.intro}
        testId="jobs-hero"
      />

      {/* Kategorie-Filter */}
      {userToken && mine.length > 0 && (
        <div className="mt-4">
          <Link to="/meine-bewerbungen" data-testid="jobs-my-applications-link" className="text-sm font-semibold text-[#b39bff] hover:text-white">
            Meine Bewerbungen & Austausch mit dem Team →
          </Link>
        </div>
      )}
      {data && data.categories.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="jobs-category-filter">
          <button
            onClick={() => setCategory(null)}
            data-testid="jobs-filter-all"
            className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              !category ? "bg-[#7c4dff] text-white" : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"
            }`}
          >
            Alle
          </button>
          {data.categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c === category ? null : c)}
              data-testid={`jobs-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                category === c ? "bg-[#7c4dff] text-white" : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Liste */}
      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-white/10 bg-[#0f141b] px-6 py-16 text-center" data-testid="jobs-empty">
          <Sparkles className="mx-auto h-8 w-8 text-zinc-600" />
          <div className="mt-3 text-lg font-semibold text-white">Aktuell keine offenen Stellen</div>
          <p className="mt-1 text-sm text-zinc-500">Schau bald wieder vorbei – neue Positionen erscheinen hier zuerst.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((j) => {
            const applied = appliedMap[j.id];
            return (
              <Link
                key={j.id}
                to={`/jobs/${j.id}`}
                data-testid={`job-card-${j.id}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b] p-6 transition-all hover:-translate-y-0.5 hover:border-[#7c4dff]/40 hover:shadow-[0_8px_40px_-12px_rgba(124,77,255,0.35)]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-full bg-[#7c4dff]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#b39bff]">
                    {j.category}
                  </span>
                  {applied && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold text-emerald-300" data-testid={`job-applied-badge-${j.id}`}>
                      <CheckCircle2 className="h-3 w-3" /> {STATUS_LABEL[applied] || "Beworben"}
                    </span>
                  )}
                </div>
                <h2 className="mt-3 text-lg font-bold text-white group-hover:text-[#c9b3ff] md:text-lg">{j.title}</h2>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-zinc-400">{j.short_description}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#b39bff] transition-colors group-hover:text-white">
                  Ansehen & Bewerben <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
